import { CalendarEvent, memberPerkConfig } from '../../../mocks/eventsCalendar';

interface OurEventsSectionProps {
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
  onRsvpClick?: (event: CalendarEvent) => void;
}

export default function OurEventsSection({ events, onEventClick, onRsvpClick }: OurEventsSectionProps) {
  if (events.length === 0) return null;

  const [featured, ...rest] = events;

  return (
    <section id="our-events" className="py-14 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-700 text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
              <span className="w-2 h-2 rounded-full bg-pink-500 inline-block"></span>
              Hosted by The Mommy Mingle
            </div>
            <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Our Events
            </h2>
            <p className="text-sm text-gray-500 mt-1">Events planned and hosted by The Mommy Mingle community</p>
          </div>
          <span className="text-xs text-gray-400 font-medium shrink-0">{events.length} upcoming</span>
        </div>

        {/* Featured large card + grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Hero Featured Card */}
          {featured && (
            <div
              onClick={() => onEventClick(featured)}
              className="relative rounded-2xl overflow-hidden h-80 cursor-pointer group border border-rose-100"
            >
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent"></div>

              {/* Date badge */}
              <div className="absolute top-4 left-4 bg-white rounded-xl px-3 py-2 text-center shadow-sm min-w-[52px]">
                <div className="text-[10px] font-bold text-pink-500 leading-none uppercase">
                  {new Date(featured.date + 'T00:00:00').toLocaleString('en-US', { month: 'short' })}
                </div>
                <div className="text-xl font-black text-gray-900 leading-tight">
                  {new Date(featured.date + 'T00:00:00').getDate()}
                </div>
              </div>

              {/* Featured badge */}
              <div className="absolute top-4 right-4 bg-pink-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                Featured
              </div>

              {/* Member Perk badge on hero card */}
              {featured.memberPerk && (
                <div className="absolute top-14 right-4 flex items-center gap-1 bg-rose-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
                  <i className="ri-vip-crown-fill text-[9px]"></i>
                  Member Perk
                </div>
              )}

              {/* Bottom content */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                {featured.memberPerk && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="flex items-center gap-1 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-2.5 py-1">
                      <i className={`${memberPerkConfig[featured.memberPerk].icon} text-white text-[10px]`}></i>
                      <span className="text-[10px] font-semibold text-white">{memberPerkConfig[featured.memberPerk].label}</span>
                    </div>
                  </div>
                )}
                <h3 className="text-white text-lg font-bold leading-snug mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {featured.title}
                </h3>
                <div className="flex items-center gap-3 text-white/75 text-xs mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {featured.time}{featured.endTime ? ` – ${featured.endTime}` : ''}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {featured.location}
                  </span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); if (onRsvpClick) onRsvpClick(featured); else onEventClick(featured); }}
                  className="px-5 py-2 bg-white text-pink-600 text-xs font-bold rounded-full hover:bg-pink-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  View Details &amp; RSVP
                </button>
              </div>
            </div>
          )}

          {/* Right column: stacked cards */}
          <div className="flex flex-col gap-4">
            {rest.slice(0, 3).map((ev) => {
              const dateObj = new Date(ev.date + 'T00:00:00');
              const month = dateObj.toLocaleString('en-US', { month: 'short' }).toUpperCase();
              const day = dateObj.getDate();
              const weekday = dateObj.toLocaleString('en-US', { weekday: 'short' });

              return (
                <div
                  key={ev.id}
                  onClick={() => onEventClick(ev)}
                  className="flex gap-4 bg-rose-50/60 hover:bg-rose-50 rounded-2xl p-4 cursor-pointer group border border-rose-100 transition-colors"
                >
                  {/* Image */}
                  <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 relative">
                    <img
                      src={ev.image}
                      alt={ev.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    {ev.memberPerk && (
                      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-0.5 bg-rose-600/90 py-0.5">
                        <i className="ri-vip-crown-fill text-white text-[8px]"></i>
                        <span className="text-[8px] font-bold text-white uppercase tracking-wide">Perk</span>
                      </div>
                    )}
                  </div>

                  {/* Date pill */}
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="text-center shrink-0 bg-white rounded-xl px-2.5 py-2 border border-rose-100 min-w-[44px]">
                      <div className="text-[9px] font-bold text-pink-500 leading-none">{month}</div>
                      <div className="text-base font-black text-gray-900 leading-tight">{day}</div>
                      <div className="text-[9px] text-gray-400 leading-none">{weekday}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-pink-600 transition-colors leading-snug mb-1">
                        {ev.title}
                      </h4>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <i className="ri-time-line text-pink-400 text-xs"></i>
                        {ev.time}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 truncate">
                        <i className="ri-map-pin-line text-pink-400 text-xs shrink-0"></i>
                        <span className="truncate">{ev.location}</span>
                      </p>
                      {ev.memberPerk && (
                        <div className="flex items-center gap-1 mt-1">
                          <i className={`${memberPerkConfig[ev.memberPerk].icon} text-rose-500 text-[10px]`}></i>
                          <span className="text-[10px] font-semibold text-rose-500">{memberPerkConfig[ev.memberPerk].label}</span>
                        </div>
                      )}
                    </div>
                    <div className="w-7 h-7 flex items-center justify-center bg-pink-100 group-hover:bg-pink-500 rounded-full transition-colors shrink-0">
                      <i className="ri-arrow-right-line text-pink-500 group-hover:text-white text-sm transition-colors"></i>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional cards row if more than 4 */}
        {rest.length > 3 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
            {rest.slice(3).map((ev) => {
              const dateObj = new Date(ev.date + 'T00:00:00');
              const month = dateObj.toLocaleString('en-US', { month: 'short' }).toUpperCase();
              const day = dateObj.getDate();

              return (
                <div
                  key={ev.id}
                  onClick={() => onEventClick(ev)}
                  className="flex gap-3 bg-rose-50/60 hover:bg-rose-50 rounded-xl p-4 cursor-pointer group border border-rose-100 transition-colors"
                >
                  <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 relative">
                    <img src={ev.image} alt={ev.title} className="w-full h-full object-cover object-top" />
                    {ev.memberPerk && (
                      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center bg-rose-600/90 py-0.5">
                        <i className="ri-vip-crown-fill text-white text-[8px]"></i>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-pink-500 mb-0.5">{month} {day}</div>
                    <h4 className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-pink-600 transition-colors leading-snug">
                      {ev.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">{ev.time} · {ev.location}</p>
                    {ev.memberPerk && (
                      <div className="flex items-center gap-1 mt-1">
                        <i className="ri-vip-crown-fill text-rose-500 text-[9px]"></i>
                        <span className="text-[10px] font-semibold text-rose-500">Member Perk</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
